import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Placeholder URLs — replace with real store URLs once the app is published
const APP_STORE_URL = 'https://apps.apple.com/app/kuze/id6761078732';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.kuze.game';

function detectPlatform(): 'ios' | 'android' | 'other' {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

export default function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<'trying' | 'fallback'>('trying');
  const platform = detectPlatform();

  const storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
  const storeName = platform === 'ios' ? 'App Store' : 'Google Play';
  const storeIcon = platform === 'ios' ? 'ri-apple-fill' : 'ri-google-play-fill';
  const storeGradient = platform === 'ios'
    ? 'from-[#8B5CF6] to-[#A419E7]'
    : 'from-[#606FE5] to-[#4758E0]';

  useEffect(() => {
    if (!code) return;

    if (platform === 'other') {
      // Desktop — just show the fallback UI immediately
      setStatus('fallback');
      return;
    }

    // Try to open the app via universal link deep path
    // If the app is installed, iOS/Android will intercept this navigation
    const appUrl = `https://kuze.app/invite/${code}`;
    window.location.href = appUrl;

    // If we're still here after 1.5s the app isn't installed → show store CTA
    const timer = setTimeout(() => setStatus('fallback'), 1500);
    return () => clearTimeout(timer);
  }, [code, platform]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-6">
      <div className="max-w-sm w-full text-center">
        {/* Logo */}
        <img src="/logo.png" alt="Kuze" className="h-12 w-auto mx-auto mb-10" />

        {status === 'trying' && platform !== 'other' ? (
          <>
            <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-gray-400 text-lg">Opening Kuze…</p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold mb-3">
              You're invited to a party!
            </h1>
            <p className="text-gray-400 mb-2">
              Room code
            </p>
            <p className="text-4xl font-bold tracking-widest text-white mb-8 font-mono">
              {code}
            </p>

            {platform !== 'other' ? (
              <>
                <p className="text-gray-400 mb-6">
                  Download Kuze to join the party.
                </p>
                <a
                  href={storeUrl}
                  className={`flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r ${storeGradient} rounded-xl text-white hover:opacity-90 transition-all shadow-lg w-full mb-4`}
                >
                  <i className={`${storeIcon} text-2xl`}></i>
                  <div className="flex flex-col items-start">
                    <span className="text-xs font-normal opacity-90">
                      {platform === 'ios' ? 'Download on the' : 'Get it on'}
                    </span>
                    <span className="text-lg font-semibold leading-tight">{storeName}</span>
                  </div>
                </a>
              </>
            ) : (
              <>
                <p className="text-gray-400 mb-6">
                  Kuze is a mobile app. Scan this code on your phone or enter the room code manually.
                </p>
                <div className="flex gap-4 justify-center">
                  <a
                    href={APP_STORE_URL}
                    className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#8B5CF6] to-[#A419E7] rounded-xl text-white hover:opacity-90 transition-all shadow-lg"
                  >
                    <i className="ri-apple-fill text-xl"></i>
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] opacity-90">Download on the</span>
                      <span className="text-sm font-semibold leading-tight">App Store</span>
                    </div>
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#606FE5] to-[#4758E0] rounded-xl text-white hover:opacity-90 transition-all shadow-lg"
                  >
                    <i className="ri-google-play-fill text-xl"></i>
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] opacity-90">Get it on</span>
                      <span className="text-sm font-semibold leading-tight">Google Play</span>
                    </div>
                  </a>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
